import React from "react";
import { CirclePlus, SquarePen, Trash2, Zap } from "lucide-react";
import { toast } from "@components/ui/use-toast";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select";
import { DefinedVariable, DropdownOption, FieldItem } from "./allData";
import CreateActionField from "./CreateActionField";
import { Button } from "@components/ui/button";
import Confirmation from "@components/common/ConfirmDialog";
import { useMutation } from "react-query";
import { sendRequest } from "./createPluginApi";
import PageLoader from "@components/loaders/PageLoader";
import FallBackUi from "./FallBackUi";
import {
  useMeshPluginContext,
  MeshPluginActions,
} from "@services/providers/MeshPlugin";

const EXCLUDE_DATA_KEYS = ["uid", "isDecript"];

type Props = {
  dropdownInput?: { label: string; value: string };
  setDropdownInput?: React.Dispatch<
    React.SetStateAction<{ label: string; value: string }>
  >;
  dropdownSelections?: { [key: number]: string };
  setDropdownSelections?: React.Dispatch<
    React.SetStateAction<{ [key: number]: string }>
  >;
  dependentSelections?: { [key: number]: string };
  setDependentSelections?: React.Dispatch<
    React.SetStateAction<{ [key: number]: string }>
  >;
  definedVariables?: any[];
  logicType?: any[];
  dropdownBlocks?: any[];
  setDropdownBlocks?: React.Dispatch<React.SetStateAction<any[]>>;
  alldropdownTrees?: {
    dropdownTrees: any[][];
    configDropdownTrees: any[][];
  };
  setAlldropdownTrees?: React.Dispatch<
    React.SetStateAction<{
      dropdownTrees: any[][];
      configDropdownTrees: any[][];
    }>
  >;
  handleSaveSettings?: () => void;
  setDefinedVariables?: React.Dispatch<React.SetStateAction<DefinedVariable[]>>;
  submissionAction?: {
    submitBundleId: string;
    submitRequest: string;
    Ismultiple?: boolean;
    defaultActionField?: string;
    selectedFieldOption?: string;
    dependencyConfigs?: Array<{
      fieldOption: string;
      submitBundleId: string;
      submitRequest: string;
    }>;
  };
  setSubmissionAction?: React.Dispatch<
    React.SetStateAction<{
      submitBundleId: string;
      submitRequest: string;
      Ismultiple?: boolean;
      defaultActionField?: string;
      selectedFieldOption?: string;
      dependencyConfigs?: Array<{
        fieldOption: string;
        submitBundleId: string;
        submitRequest: string;
      }>;
    }>
  >;
  meshVersion?: string;
  settingsWorkflows?: any[];
  actionWorkflows?: any[];
  className?: string;
};

const ActionsPage: React.FC<Props> = ({
  dropdownInput,
  setDropdownInput,
  dropdownSelections,
  setDropdownSelections,
  dependentSelections,
  setDependentSelections,
  definedVariables,
  logicType,
  dropdownBlocks,
  setDropdownBlocks,
  alldropdownTrees,
  setAlldropdownTrees,
  handleSaveSettings,
  setDefinedVariables,
  submissionAction,
  setSubmissionAction,
  meshVersion,
  settingsWorkflows,
  actionWorkflows,
  className,
}) => {
  const {
    fieldName,
    fieldType,
    fields,
    dropdownOptions,
    hasUnsavedFields,
    inputDefaultValue,
    savedSettings,
    showForm,
    dispatch,
  } = useMeshPluginContext();
  const [dependsOnField, setDependsOnField] = React.useState<string>("");
  const [editIndex, setEditIndex] = React.useState<number | null>(null);
  const [isApiValue, setIsApiValue] = React.useState(false);
  const [configValue, setConfigValue] = React.useState<any>(null);
  const [isFromVariable, setIsFromVariable] = React.useState(false);
  const [isFromAnomaly, setIsFromAnomaly] = React.useState(false);
  const [mainDropdownOptions, setMainDropdownOptions] = React.useState<
    DropdownOption[]
  >([]);
  const [targetDropdownMap, setTargetDropdownMap] = React.useState<{
    [targetField: string]: DropdownOption[];
  }>({});
  const [dropdownChains, setDropdownChains] = React.useState<{
    [key: number]: { [key: string | number]: string };
  }>({});
  const [editingOptionFieldIndex, setEditingOptionFieldIndex] = React.useState<
    number | null
  >(null);
  const [editingOptionIndex, setEditingOptionIndex] = React.useState<
    number | null
  >(null);
  const [deleteActionModalOpen, setDeleteActionModalOpen] =
    React.useState(false);
  const [actionToDeleteIndex, setActionToDeleteIndex] = React.useState<
    number | null
  >(null);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [initialFieldIds, setInitialFieldIds] = React.useState<string[]>([]);
  const [shouldResetFieldIds, setShouldResetFieldIds] = React.useState(false);
  const [isInitialized, setIsInitialized] = React.useState(false);
  const justInitializedRef = React.useRef(false);

  const [currentRefreshIndex, setCurrentRefreshIndex] =
    React.useState<number>(0);

  const refreshQueueRef = React.useRef<DefinedVariable[]>([]);
  const selectedConfigRef = React.useRef<any>(null);

  // For mesh version 2.0, prepare workflows as bundles for compatibility
  const actionBundlesOrWorkflows = React.useMemo(() => {
    if (meshVersion === "2.0" && actionWorkflows) {
      return actionWorkflows.map((workflow: any) => ({
        bundleId: workflow.uid,
        bundleTitle: workflow.title,
        bundleType: "action",
        requests: [], // Workflows don't have nested requests
        workflow: workflow, // Include full workflow object for execution
      }));
    }
    const actionLogic = logicType?.find((l: any) => l.name === "Action");
    return actionLogic?.data || [];
  }, [meshVersion, actionWorkflows, logicType]);

  const settingsBundlesOrWorkflows = React.useMemo(() => {
    if (meshVersion === "2.0" && settingsWorkflows) {
      return settingsWorkflows.map((workflow: any) => ({
        bundleId: workflow.uid,
        bundleTitle: workflow.title,
        bundleType: "settings",
        requests: [], // Workflows don't have nested requests
        workflow: workflow, // Include full workflow object for execution
      }));
    }
    const settingsLogic = logicType?.find((l: any) => l.name === "Settings");
    return settingsLogic?.data || [];
  }, [meshVersion, settingsWorkflows, logicType]);

  // Mutation to refresh authentication variable
  const setAuthVariable = useMutation({
    mutationFn: (payload: any) => sendRequest(payload),

    onMutate: () => {
      setLoading(true);
    },

    onSettled: () => {
      setLoading(false);
    },

    onSuccess: (response: any) => {
      //  Get current variable from refreshQueue using currentRefreshIndex
      const currentVariable = refreshQueueRef?.current[currentRefreshIndex];

      if (!currentVariable || !definedVariables) {
        processNextVariable();
        return;
      }

      // Extract base response value
      const resValue = response?.body?.data ?? response?.data ?? response;

      const path = currentVariable?.sourcePath || "";
      let currentValue = resValue;

      // Handle path navigation with array support
      if (path && path !== "root") {
        // Convert array notation [0] to .0 for easier splitting
        const normalizedPath = path.replace(/\[(\d+)\]/g, ".$1");
        const pathParts = normalizedPath.split(".").filter(Boolean);

        for (const part of pathParts) {
          if (currentValue === undefined || currentValue === null) {
            break;
          }

          const index = Number(part);

          if (!isNaN(index) && Array.isArray(currentValue)) {
            currentValue = currentValue[index];
          } else if (
            typeof currentValue === "object" &&
            currentValue !== null &&
            part in currentValue
          ) {
            currentValue = currentValue[part];
          } else {
            break;
          }
        }
      }

      if (currentValue === undefined) {
        toast({
          title: "Variable Refresh Failed",
          description: `Path "${currentVariable.sourcePath}" not found in response`,
          variant: "destructive",
          duration: 4000,
        });

        console.warn("Path resolution failed:", {
          variable: currentVariable.name,
          path: currentVariable.sourcePath,
          response,
        });

        processNextVariable();
        return;
      }

      //  Update the variable value in definedVariables state
      const updatedVariables = definedVariables?.map((variable) => {
        if (variable?.name === currentVariable?.name) {
          return {
            ...variable,
            value: currentValue,
          };
        }
        return variable;
      });

      setDefinedVariables?.(updatedVariables);

      // Process next variable
      processNextVariable();
    },

    onError: (error: any) => {
      const currentVariable = refreshQueueRef?.current?.[currentRefreshIndex];

      toast({
        title: "Variable Refresh Failed",
        description: currentVariable
          ? `Failed to refresh variable "${currentVariable.name}"`
          : "Failed to refresh variable",
        variant: "destructive",
        duration: 4000,
      });

      console.error("Variable refresh error:", error);

      processNextVariable();
    },
  });

  // Generate a unique ID for each field based on its content
  const generateFieldId = (field: FieldItem): string => {
    return JSON.stringify({
      name: field.fieldName,
      type: field.fieldType,
      default: field.defaultValue,
      options: field.dropdownOptions,
      submitConfig: field.submitConfig,
    });
  };

  // Initialize field IDs on mount - only once when fields are loaded
  React.useEffect(() => {
    if (!isInitialized && fields) {
      const ids = fields.map(generateFieldId);
      setInitialFieldIds(ids);
      setIsInitialized(true);
      justInitializedRef.current = true;
      dispatch({
        type: MeshPluginActions.SET_HAS_UNSAVED_FIELDS,
        payload: false,
      });
    }
  }, [fields, isInitialized]);

  // Reset field IDs after save
  React.useEffect(() => {
    if (shouldResetFieldIds && fields) {
      const ids = fields.map(generateFieldId);
      setInitialFieldIds(ids);
      setShouldResetFieldIds(false);
      justInitializedRef.current = true;
      dispatch({
        type: MeshPluginActions.SET_HAS_UNSAVED_FIELDS,
        payload: false,
      });
    }
  }, [shouldResetFieldIds, fields]);

  // Reset baseline when parent indicates save completed (hasUnsavedFields becomes false)
  React.useEffect(() => {
    if (isInitialized && hasUnsavedFields === false && fields) {
      const ids = fields.map(generateFieldId);
      // Only update if IDs actually changed
      const idsChanged =
        ids.length !== initialFieldIds.length ||
        ids.some((id, index) => id !== initialFieldIds[index]);
      if (idsChanged) {
        setInitialFieldIds(ids);
        justInitializedRef.current = true;
      }
    }
  }, [hasUnsavedFields]);

  // Track unsaved fields by comparing field IDs
  React.useEffect(() => {
    // Skip check if we just initialized
    if (justInitializedRef.current) {
      justInitializedRef.current = false;
      return;
    }

    // Don't check until initialized
    if (!isInitialized) {
      return;
    }

    if (initialFieldIds.length === 0 && (!fields || fields.length === 0)) {
      dispatch({
        type: MeshPluginActions.SET_HAS_UNSAVED_FIELDS,
        payload: false,
      });
      return;
    }

    const currentFieldIds = fields?.map(generateFieldId) || [];

    // If both arrays are empty, no changes
    if (currentFieldIds.length === 0 && initialFieldIds.length === 0) {
      dispatch({
        type: MeshPluginActions.SET_HAS_UNSAVED_FIELDS,
        payload: false,
      });
      return;
    }

    // Check if arrays are different (length or content)
    const hasChanges =
      currentFieldIds.length !== initialFieldIds.length ||
      currentFieldIds.some((id, index) => id !== initialFieldIds[index]) ||
      initialFieldIds.some((id, index) => id !== currentFieldIds[index]);

    dispatch({
      type: MeshPluginActions.SET_HAS_UNSAVED_FIELDS,
      payload: hasChanges,
    });
  }, [fields, initialFieldIds, isInitialized]);

  // Fetch and store resValues when selection changes
  React.useEffect(() => {
    Object.entries(dropdownSelections ?? {}).forEach(([idx, selectedVal]) => {
      const index = parseInt(idx);
      const field = fields?.[index];
      if (!field || !field.dropdownOptions) return;

      const optionIndex = field.dropdownOptions.findIndex(
        (opt) => opt.value === selectedVal,
      );
      const selectedOption = field.dropdownOptions[optionIndex];
      if (!selectedOption?.apiEndpoint) return;

      fetch(selectedOption.apiEndpoint)
        .then((res) => res.json())
        .then((data: any) => {
          const items = data?.products || data?.items || data || [];
          const newResValues = (items as any[]).map((item) => ({
            label: item.title ?? item?.label ?? "No Label",
            value: item.id?.toString() ?? item?.value ?? "no-value",
          }));

          const updatedFields = [...fields!];
          updatedFields[index] = {
            ...field,
            dropdownOptions: field.dropdownOptions!.map((opt, idx2) =>
              idx2 === optionIndex ? { ...opt, resValues: newResValues } : opt,
            ),
          };

          dispatch({
            type: MeshPluginActions.SET_ACTION_FIELDS,
            payload: updatedFields,
          });
        })
        .catch(console.error);
    });
  }, [dropdownSelections]);

  // Function to add a new dropdown option
  const handleAddDropdownOption = () => {
    if (!dropdownInput?.label || !dropdownInput?.value) {
      toast({
        variant: "destructive",
        title: "Missing Fields",
        description: "Please provide both label and value.",
      });
      return;
    }

    if (editingOptionFieldIndex !== null && editingOptionIndex !== null) {
      dispatch({
        type: MeshPluginActions.SET_ACTION_FIELDS,
        payload: (prevFields: FieldItem[]) => {
          const updatedFields = [...prevFields];
          const field = updatedFields[editingOptionFieldIndex];
          const optionToUpdate: any =
            field.dropdownOptions?.[editingOptionIndex];

          if (field.dropdownOptions) {
            field.dropdownOptions[editingOptionIndex] = {
              ...optionToUpdate,
              label: dropdownInput.label,
              value: dropdownInput.value,
              fromVariable: isFromVariable,
              isAnomalies: isFromAnomaly,
              fromAnomaly: isFromAnomaly,
              isApiValue: isApiValue,
              apiEndpoint: isApiValue ? optionToUpdate?.apiEndpoint : undefined,
            };
          }

          return updatedFields;
        },
      });

      // Clear editing state
      setEditingOptionFieldIndex(null);
      setEditingOptionIndex(null);
    } else {
      const exists = dropdownOptions?.some(
        (opt) => opt.value === dropdownInput.value,
      );

      if (exists) {
        toast({
          variant: "destructive",
          title: "Duplicate Option",
          description: "This value already exists in options.",
        });
        return;
      }

      dispatch({
        type: MeshPluginActions.SET_DROPDOWN_OPTIONS,
        payload: (prev: DropdownOption[]) => [
          ...prev,
          {
            ...dropdownInput,
            isAnomalies: isFromAnomaly,
            fromVariable: isFromVariable,
          },
        ],
      });
    }

    // Reset input
    setDropdownInput?.({ label: "", value: "" });
    setIsFromAnomaly(false);
    setIsFromVariable(false);
    setIsApiValue(false);
  };

  // Function to remove a dropdown option by index
  const handleRemoveDropdownOption = (index: number) => {
    dispatch({
      type: MeshPluginActions.SET_DROPDOWN_OPTIONS,
      payload: (prev: DropdownOption[]) => prev.filter((_, i) => i !== index),
    });
  };

  // Function to add or update a field
  const handleAddField = () => {
    if (
      !fieldName?.trim() ||
      (fieldType === "input" && !inputDefaultValue?.trim())
    ) {
      toast({
        variant: "destructive",
        title: "Missing Fields",
        description: "Please fill in all required fields.",
      });
      return;
    }

    // Check for duplicate field names
    const trimmedFieldName = fieldName.trim();
    if (trimmedFieldName.toLowerCase() === "submit" && fieldType !== "submit") {
      toast({
        variant: "destructive",
        title: "Reserved Submit Name",
        description:
          "Submit/submit is allowed only when Field Type is Action Submission Button.",
        duration: 3000,
      });
      return;
    }

    const isDuplicate = fields?.some((field, index) => {
      // When editing, exclude the current field being edited
      if (editIndex !== null && index === editIndex) {
        return false;
      }
      return (
        field.fieldName &&
        field.fieldName.toLowerCase().trim() === trimmedFieldName.toLowerCase()
      );
    });

    if (isDuplicate) {
      toast({
        variant: "destructive",
        title: "Duplicate Field Name",
        description: `The field name "${trimmedFieldName}" already exists. Please use a unique name.`,
        duration: 3000,
      });
      return;
    }

    // Add validation for submit button
    if (fieldType === "submit") {
      if (submissionAction?.Ismultiple) {
        // Validate dependency mode
        if (!submissionAction.defaultActionField) {
          toast({
            variant: "destructive",
            title: "Missing Configuration",
            description:
              "Please select a field for dependency-based submission.",
          });
          return;
        }

        if (
          !submissionAction.dependencyConfigs ||
          submissionAction.dependencyConfigs.length === 0
        ) {
          toast({
            variant: "destructive",
            title: "Missing Configuration",
            description: "Please configure at least one option with a bundle.",
          });
          return;
        }

        // Check that all configs have bundles
        const missingBundles = submissionAction.dependencyConfigs.filter(
          (config) =>
            !config.submitBundleId || config.submitBundleId.trim() === "",
        );

        if (missingBundles.length > 0) {
          toast({
            variant: "destructive",
            title: "Incomplete Configuration",
            description: `${missingBundles.length} option(s) are missing bundle selection. Please select a bundle for all options.`,
          });
          return;
        }
      } else {
        // Validate normal mode
        if (
          !submissionAction?.submitBundleId ||
          submissionAction.submitBundleId.trim() === ""
        ) {
          toast({
            variant: "destructive",
            title: "Missing Configuration",
            description: "Please select at least a bundle for submit button.",
          });
          return;
        }
      }
    }

    // Better validation for dropdown types
    if (fieldType === "dropdown") {
      const hasApiDropdown =
        isApiValue &&
        alldropdownTrees?.dropdownTrees &&
        alldropdownTrees.dropdownTrees.length > 0;
      const hasConfigDropdown =
        configValue &&
        alldropdownTrees?.configDropdownTrees &&
        alldropdownTrees.configDropdownTrees.length > 0;
      const hasManualOptions =
        !isApiValue &&
        !configValue &&
        dropdownOptions &&
        dropdownOptions.length > 0;

      if (!hasApiDropdown && !hasConfigDropdown && !hasManualOptions) {
        toast({
          variant: "destructive",
          title: "No Dropdown Data",
          description: "Please add at least one dropdown option or logic tree.",
        });
        return;
      }

      // Validate API dropdown tree structure
      if (hasApiDropdown) {
        for (const tree of alldropdownTrees.dropdownTrees) {
          if (!Array.isArray(tree)) continue;
          for (const node of tree) {
            if (!node.label || !node.value) {
              toast({
                variant: "destructive",
                title: "Incomplete Node",
                description:
                  "All nodes in the dropdown logic tree must have both label and value.",
              });
              return;
            }
          }
        }
      }

      // Validate config dropdown tree structure
      if (hasConfigDropdown) {
        for (const tree of alldropdownTrees.configDropdownTrees) {
          if (!Array.isArray(tree)) continue;
          for (const node of tree) {
            if (!node.label || !node.value) {
              toast({
                variant: "destructive",
                title: "Incomplete Node",
                description:
                  "All config nodes must have both label and value (settings configuration selected).",
              });
              return;
            }
          }
        }
      }
    }

    // Rest of the function remains the same...
    const newField: FieldItem = {
      fieldName,
      fieldType: fieldType ?? "select",
      defaultValue: inputDefaultValue,
      dropdownOptions,

      dropdownTree:
        isApiValue &&
        alldropdownTrees?.dropdownTrees &&
        alldropdownTrees.dropdownTrees.length > 0
          ? alldropdownTrees.dropdownTrees.flat().map((node) => ({
              ...node,
              targetFields:
                node.targetFields?.map((tf: any) => ({
                  ...tf,
                  previewOptions:
                    tf.previewOptions?.map((opt: any) => ({
                      ...opt,
                      targetFields: opt.targetFields || [],
                      children: opt.children || [],
                    })) || [],
                })) || [],
            }))
          : undefined,
      configDropdownTree:
        configValue &&
        alldropdownTrees?.configDropdownTrees &&
        alldropdownTrees.configDropdownTrees.length > 0
          ? alldropdownTrees.configDropdownTrees.flat().map((node) => ({
              ...node,
              targetFields:
                node.targetFields?.map((tf: any) => ({
                  ...tf,
                  previewOptions:
                    tf.previewOptions?.map((opt: any) => ({
                      ...opt,
                      targetFields: opt.targetFields || [],
                      children: opt.children || [],
                    })) || [],
                })) || [],
            }))
          : undefined,

      fromVariable: isFromVariable,
      fromAnomaly: isFromAnomaly,
      isApiValue: isApiValue,
      isConfig: configValue,

      ...(fieldType === "submit" && {
        submitConfig: {
          Ismultiple: submissionAction?.Ismultiple || false,
          bundleId: submissionAction?.submitBundleId || "",
          requestId: submissionAction?.submitRequest || "all_bundle_requests",
          submit_types: submissionAction?.Ismultiple
            ? (submissionAction?.dependencyConfigs || []).map(
                (config: any) => ({
                  field_given_name: submissionAction?.defaultActionField || "",
                  option_given_name: config.fieldOption || "",
                  bundleId: config.submitBundleId || "",
                  requestId: config.submitRequest || "all_bundle_requests",
                }),
              )
            : [],
        },
      }),
    };

    if (editIndex !== null) {
      dispatch({
        type: MeshPluginActions.SET_ACTION_FIELDS,
        payload: (prev: FieldItem[]) => {
          const updated = [...prev];
          updated[editIndex] = newField;
          return updated;
        },
      });
    } else {
      dispatch({
        type: MeshPluginActions.SET_ACTION_FIELDS,
        payload: (prev: FieldItem[]) => [...prev, newField],
      });
    }

    // Reset form
    dispatch({ type: MeshPluginActions.SET_FIELD_NAME, payload: "" });
    dispatch({ type: MeshPluginActions.SET_FIELD_TYPE, payload: "select" });
    setMainDropdownOptions([]);
    setTargetDropdownMap({});
    setDropdownInput?.({ label: "", value: "" });
    dispatch({ type: MeshPluginActions.SET_SHOW_FORM, payload: false });
    dispatch({ type: MeshPluginActions.SET_INPUT_DEFAULT_VALUE, payload: "" });
    setIsApiValue?.(false);
    setIsFromVariable?.(false);
    setIsFromAnomaly?.(false);
    setEditIndex(null);
    dispatch({ type: MeshPluginActions.SET_DROPDOWN_OPTIONS, payload: [] });
    setConfigValue?.(null);
    setSubmissionAction?.({
      submitBundleId: "",
      submitRequest: "",
      Ismultiple: false,
      defaultActionField: "",
      selectedFieldOption: "",
      dependencyConfigs: [],
    });
    setAlldropdownTrees?.({ dropdownTrees: [], configDropdownTrees: [] });
  };

  // Function to remove a field by index
  const removeField = (index: number) => {
    setActionToDeleteIndex(index);
    setDeleteActionModalOpen(true);
  };
  const onDelete = () => {
    if (actionToDeleteIndex !== null) {
      dispatch({
        type: MeshPluginActions.SET_ACTION_FIELDS,
        payload: (prevFields: FieldItem[]) => {
          const updatedFields = [...(prevFields ?? [])];
          updatedFields.splice(actionToDeleteIndex, 1);
          return updatedFields;
        },
      });

      // Clean up dropdownSelections
      setDropdownSelections?.((prev) => {
        const newState = { ...prev };
        delete newState[actionToDeleteIndex];

        // Shift all indexes after the deleted one down by 1
        const shiftedState: { [key: number]: string } = {};
        Object.entries(newState).forEach(([key, value]) => {
          const numKey = parseInt(key);
          shiftedState[numKey > actionToDeleteIndex ? numKey - 1 : numKey] =
            value;
        });

        return shiftedState;
      });
    }
    setDeleteActionModalOpen(false);
    setActionToDeleteIndex(null);
    toast({
      title: "Action Locally Deleted",
      description: "Action field has been removed. Save changes.",
      variant: "success",
      duration: 3000,
    });
  };

  // Close form and reset
  const handleClose = () => {
    dispatch({ type: MeshPluginActions.SET_SHOW_FORM, payload: false });
    setEditIndex(null);
    dispatch({ type: MeshPluginActions.SET_FIELD_NAME, payload: "" });
    dispatch({ type: MeshPluginActions.SET_FIELD_TYPE, payload: "select" });
    dispatch({ type: MeshPluginActions.SET_DROPDOWN_OPTIONS, payload: [] });
    setDropdownInput?.({ label: "", value: "" });
    dispatch({ type: MeshPluginActions.SET_INPUT_DEFAULT_VALUE, payload: "" });
    setDependsOnField?.("");
    setIsApiValue?.(false);
    setIsFromVariable?.(false);
    setSubmissionAction?.({
      submitBundleId: "",
      submitRequest: "",
      Ismultiple: false,
      defaultActionField: "",
      selectedFieldOption: "",
      dependencyConfigs: [],
    });
    setAlldropdownTrees?.({ dropdownTrees: [], configDropdownTrees: [] });
  };

  // Recursive rendering of dropdown tree levels with targetFields support
  const renderDropdownTreeLevels = (
    node: any,
    fieldIndex: number | string,
    level: number,
    parentChain: string[] = [],
  ): React.ReactNode => {
    if (!node) return null;

    // Get options from children
    const options =
      node.children?.map((child: any) => ({
        label: child.label,
        value: child.value,
      })) ?? [];

    //  Check if current level has a selection
    const selectedValue = dropdownChains?.[Number(fieldIndex)]?.[level] || "";

    // If no children but has targetFields, render target fields ONLY if parent is selected
    if (
      options.length === 0 &&
      node.targetFields &&
      node.targetFields.length > 0
    ) {
      return (
        <div
          key={`${node.value}-targetFields`}
          className="mt-3 border-l-2 border-primaryBlue/30"
        >
          {node.targetFields.map((targetField: any, targetIdx: number) => {
            const targetFieldKey = `${fieldIndex}-${level}-${targetIdx}`;
            const targetSelectedValue =
              dropdownChains?.[Number(fieldIndex)]?.[
                `target-${level}-${targetIdx}`
              ] || "";

            return (
              <div key={targetFieldKey}>
                <label className="ml-1 font-medium text-sm">
                  {targetField.fieldName}
                </label>
                <Select
                  value={targetSelectedValue}
                  onValueChange={(val) => {
                    setDropdownChains?.((prev) => {
                      const updated = { ...prev };
                      if (!updated[Number(fieldIndex)]) {
                        updated[Number(fieldIndex)] = {};
                      }
                      updated[Number(fieldIndex)][
                        `target-${level}-${targetIdx}`
                      ] = val;
                      return updated;
                    });
                  }}
                >
                  <SelectTrigger className="max-w-[450px]">
                    <SelectValue
                      placeholder={targetField.fieldName || "Select an option"}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value="select">Select an option</SelectItem>
                      {(targetField.previewOptions || []).map((opt: any) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>

                {/*  Recursively render nested targetFields ONLY if this option is selected */}
                {targetSelectedValue &&
                  targetSelectedValue !== "select" &&
                  (() => {
                    const selectedOption = targetField.previewOptions?.find(
                      (opt: any) => opt.value === targetSelectedValue,
                    );
                    if (
                      selectedOption?.targetFields &&
                      selectedOption.targetFields.length > 0
                    ) {
                      return renderDropdownTreeLevels(
                        selectedOption,
                        fieldIndex,
                        level + 1,
                        [...parentChain, targetSelectedValue],
                      );
                    }
                    return null;
                  })()}
              </div>
            );
          })}
        </div>
      );
    }

    // If no more children and no targetFields, stop recursion
    if (options.length === 0) return null;

    return (
      <div key={`${node.value}-${level}`} className="mt-2">
        <label className="ml-1">{node.target || `Level ${level + 1}`}</label>
        <Select
          value={selectedValue}
          onValueChange={(val) => {
            setDropdownChains?.((prev) => {
              const prevChain = prev?.[Number(fieldIndex)] || {};
              const updated = { ...prevChain };
              updated[level] = val;
              // Clear all subsequent levels
              Object.keys(updated).forEach((key) => {
                const keyNum = parseInt(key);
                if (!isNaN(keyNum) && keyNum > level) {
                  delete updated[key];
                }
                // Also clear target fields at current level and beyond
                if (
                  key.startsWith(`target-`) &&
                  parseInt(key.split("-")[1]) >= level
                ) {
                  delete updated[key];
                }
              });
              return { ...prev, [fieldIndex]: updated };
            });
          }}
        >
          <SelectTrigger className="max-w-[450px]">
            <SelectValue placeholder="Select an option" />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectItem key="placeholder" value="select">
                Select an option
              </SelectItem>
              {options.map((opt: any) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>

        {/*  Recurse into selected child ONLY if value is selected and not placeholder */}
        {selectedValue &&
          selectedValue !== "select" &&
          (() => {
            const selectedNode = node.children?.find(
              (child: any) => child.value === selectedValue,
            );
            if (selectedNode) {
              // First render child's children
              const childrenRender =
                selectedNode.children && selectedNode.children.length > 0
                  ? renderDropdownTreeLevels(
                      selectedNode,
                      fieldIndex,
                      level + 1,
                      [...parentChain, selectedValue],
                    )
                  : null;

              // Then render targetFields if they exist
              const targetFieldsRender =
                selectedNode.targetFields &&
                selectedNode.targetFields.length > 0
                  ? renderDropdownTreeLevels(
                      selectedNode,
                      fieldIndex,
                      level + 1,
                      [...parentChain, selectedValue],
                    )
                  : null;

              return (
                <>
                  {childrenRender}
                  {targetFieldsRender}
                </>
              );
            }
            return null;
          })()}
      </div>
    );
  };

  // Get target label for dependent dropdown
  const getTargetLabel = (field: FieldItem, index: number): string => {
    const selectedOption: any = field.dropdownOptions?.find(
      (opt) => opt.value === dropdownSelections?.[index],
    );
    return selectedOption?.target || "Dependent Option";
  };

  // Edit existing dropdown option
  const editDropdownOption = (fieldIndex: number, optionIndex: number) => {
    const field = fields?.[fieldIndex];
    const option: any = field?.dropdownOptions?.[optionIndex];
    if (!field || !option) return;

    dispatch({ type: MeshPluginActions.SET_SHOW_FORM, payload: true });
    dispatch({
      type: MeshPluginActions.SET_FIELD_NAME,
      payload: field.fieldName || "",
    }); // optional
    dispatch({
      type: MeshPluginActions.SET_FIELD_TYPE,
      payload: field.fieldType || "select",
    }); // optional

    // Prefill the dropdown option fields
    setDropdownInput?.({ label: option.label, value: option.value });
    setIsApiValue?.(!!option.isApiValue);
    setIsFromAnomaly?.(!!option.fromAnomaly);
    setIsFromVariable?.(!!option.fromVariable);

    // Set editing state
    setEditingOptionFieldIndex(fieldIndex);
    setEditingOptionIndex(optionIndex);
  };

  // Edit existing dropdown option
  const handleEdit = (index: number) => {
    const fieldToEdit = fields?.[index];
    if (!fieldToEdit) return;

    // Set basic field data
    dispatch({
      type: MeshPluginActions.SET_FIELD_NAME,
      payload: fieldToEdit.fieldName,
    });
    dispatch({
      type: MeshPluginActions.SET_FIELD_TYPE,
      payload: fieldToEdit.fieldType,
    });
    dispatch({
      type: MeshPluginActions.SET_INPUT_DEFAULT_VALUE,
      payload: fieldToEdit.defaultValue || "",
    });

    //  Check for trees first, then flags
    const hasConfigDropdownTree =
      Array.isArray(fieldToEdit.configDropdownTree) &&
      fieldToEdit.configDropdownTree.length > 0;

    const hasDropdownTree =
      Array.isArray(fieldToEdit.dropdownTree) &&
      fieldToEdit.dropdownTree.length > 0;

    //  Config Dropdown (Settings-based)
    if (hasConfigDropdownTree || fieldToEdit.isConfig) {
      const isAlreadyWrapped =
        Array.isArray(fieldToEdit?.configDropdownTree?.[0]) &&
        Array.isArray(fieldToEdit.configDropdownTree[0]);

      const formattedTrees: any = isAlreadyWrapped
        ? fieldToEdit.configDropdownTree
        : (fieldToEdit.configDropdownTree || []).map((node: any) => [node]);

      setAlldropdownTrees?.({
        dropdownTrees: [],
        configDropdownTrees: formattedTrees,
      });
      dispatch({ type: MeshPluginActions.SET_DROPDOWN_OPTIONS, payload: [] }); // Clear manual options
      setIsApiValue?.(false); //  Not API
      setConfigValue?.(true); //  IS Config
      setIsFromVariable?.(false);
      setIsFromAnomaly?.(false);
    }
    //  API Dropdown (Bundle-based)
    else if (hasDropdownTree || fieldToEdit.isApiValue) {
      const isAlreadyWrapped =
        Array.isArray(fieldToEdit?.dropdownTree?.[0]) &&
        Array.isArray(fieldToEdit.dropdownTree[0]);

      const formattedTrees: any = isAlreadyWrapped
        ? fieldToEdit.dropdownTree
        : (fieldToEdit.dropdownTree || []).map((node: any) => [node]);

      setAlldropdownTrees?.({
        dropdownTrees: formattedTrees,
        configDropdownTrees: [],
      });
      dispatch({ type: MeshPluginActions.SET_DROPDOWN_OPTIONS, payload: [] }); // Clear manual options
      setIsApiValue?.(true); //  IS API
      setConfigValue?.(null); //  Not Config
      setIsFromVariable?.(false);
      setIsFromAnomaly?.(false);
    }
    //  Anomaly Dropdown
    else if (fieldToEdit.fromAnomaly) {
      dispatch({
        type: MeshPluginActions.SET_DROPDOWN_OPTIONS,
        payload: fieldToEdit.dropdownOptions || [],
      });
      setAlldropdownTrees?.({ dropdownTrees: [], configDropdownTrees: [] });
      setIsApiValue?.(false);
      setConfigValue?.(null);
      setIsFromVariable?.(false);
      setIsFromAnomaly?.(true);
    }
    //  Variable Dropdown
    else if (fieldToEdit.fromVariable) {
      dispatch({
        type: MeshPluginActions.SET_DROPDOWN_OPTIONS,
        payload: fieldToEdit.dropdownOptions || [],
      });
      setAlldropdownTrees?.({ dropdownTrees: [], configDropdownTrees: [] });
      setIsApiValue?.(false);
      setConfigValue?.(null);
      setIsFromVariable?.(true);
      setIsFromAnomaly?.(false);
    }
    //  Default/Manual Dropdown
    else {
      dispatch({
        type: MeshPluginActions.SET_DROPDOWN_OPTIONS,
        payload: fieldToEdit.dropdownOptions || [],
      });
      setAlldropdownTrees?.({ dropdownTrees: [], configDropdownTrees: [] });
      setIsApiValue?.(false);
      setConfigValue?.(null);
      setIsFromVariable?.(false);
      setIsFromAnomaly?.(false);
    }

    //  Handle submit button config
    if (fieldToEdit.fieldType === "submit" && fieldToEdit?.submitConfig) {
      const config = fieldToEdit.submitConfig;

      if (
        config.Ismultiple &&
        config.submit_types &&
        config.submit_types.length > 0
      ) {
        // Dependency mode
        const dependencyConfigs = config.submit_types.map((item: any) => ({
          fieldOption: item.option_given_name || "",
          submitBundleId: item.bundleId || "",
          submitRequest: item.requestId || "",
        }));

        setSubmissionAction?.({
          Ismultiple: true,
          defaultActionField: config.submit_types[0]?.field_given_name || "",
          selectedFieldOption: "",
          dependencyConfigs: dependencyConfigs,
          submitBundleId: "",
          submitRequest: "",
        });
      } else {
        // Normal mode
        setSubmissionAction?.({
          Ismultiple: false,
          submitBundleId: config.bundleId || "",
          submitRequest: config.requestId || "",
          defaultActionField: "",
          selectedFieldOption: "",
          dependencyConfigs: [],
        });
      }
    } else {
      // Clear submission action if not a submit button
      setSubmissionAction?.({
        submitBundleId: "",
        submitRequest: "",
        Ismultiple: false,
        defaultActionField: "",
        selectedFieldOption: "",
        dependencyConfigs: [],
      });
    }

    setEditIndex(index);
    dispatch({ type: MeshPluginActions.SET_SHOW_FORM, payload: true });
  };

  // Fetch and store resValues when selection changes - updated to check isApiValue
  React.useEffect(() => {
    Object.entries(dropdownSelections ?? {}).forEach(([idx, selectedVal]) => {
      const index = parseInt(idx);
      const field = fields?.[index];
      if (!field || !field.dropdownOptions) return;

      const optionIndex = field.dropdownOptions.findIndex(
        (opt) => opt.value === selectedVal,
      );
      const selectedOption: any = field.dropdownOptions[optionIndex];

      //  New check: Don't fetch unless it's an API value
      if (!selectedOption?.isApiValue || !selectedOption?.apiEndpoint) return;

      fetch(selectedOption.apiEndpoint)
        .then((res) => res.json())
        .then((data: any) => {
          const items = data?.products || data?.items || data || [];
          const newResValues = (items as any[]).map((item) => ({
            label: item.title ?? item?.label ?? "No Label",
            value: item.id?.toString() ?? item?.value ?? "no-value",
          }));

          const updatedFields = [...fields!];
          updatedFields[index] = {
            ...field,
            dropdownOptions: field.dropdownOptions!.map((opt, idx2) =>
              idx2 === optionIndex ? { ...opt, resValues: newResValues } : opt,
            ),
          };

          dispatch({
            type: MeshPluginActions.SET_ACTION_FIELDS,
            payload: updatedFields,
          });
        })
        .catch(console.error);
    });
  }, [dropdownSelections]);

  // Add this interpolation function near the top of FormWrapper component (after the imports)
  function interpolateDefinedVariablesOnly(
    input: string,
    definedVars: DefinedVariable[] = [],
  ): string {
    if (!input || typeof input !== "string") return input;

    return input.replace(/{{\s*(.*?)\s*}}/g, (match, key) => {
      // Skip config variables
      if (key.startsWith("config.")) return match;

      // Handle array-style access: values[1].id
      const arrayMatch = key.match(/^(\w+)\[(\d+)\]\.(\w+)$/);
      if (arrayMatch) {
        const [, arrayName, indexStr, prop] = arrayMatch;
        const variable = definedVars.find((v) => v.name === arrayName);
        if (!variable || !Array.isArray(variable.value)) return match;

        const index = parseInt(indexStr, 10);
        const item = variable.value[index];
        if (!item || !(prop in item)) return match;

        return String(item[prop]);
      }

      // Simple variable (no array index)
      const found = definedVars.find((v) => v.name === key);
      return found?.value != null ? String(found.value) : match;
    });
  }

  // Helper function to process next variable in queue
  const processNextVariable = () => {
    const nextIndex = currentRefreshIndex + 1;

    if (nextIndex < refreshQueueRef?.current?.length) {
      setCurrentRefreshIndex(nextIndex);
      refreshSingleVariable(
        refreshQueueRef?.current[nextIndex],
        selectedConfigRef.current,
      );
    } else {
      // All variables processed - show summary

      toast({
        title: "Refresh Completed",
        description: `Action Variable${refreshQueueRef?.current?.length !== 1 ? "s" : ""} refreshed successfully.`,
        variant: "success",
        duration: 5000,
      });

      // Reset state
      refreshQueueRef.current = [];
      selectedConfigRef.current = null;
      setCurrentRefreshIndex(0);
    }
  };

  // refresh single variable
  const refreshSingleVariable = (
    variable: DefinedVariable,
    selectedConfig?: any,
  ) => {
    const bundleId = variable?.sourceBundleId;
    const requestId = variable?.sourceRequestId;

    if (variable.variableType !== "Action") {
      processNextVariable();
      return;
    }

    const allBundles =
      logicType?.find((logic) => logic?.name === "Action")?.data || [];

    const bundle = allBundles?.find(
      (b: any) =>
        b?.id == bundleId ||
        b?.bundleId == bundleId ||
        String(b?.id) === String(bundleId) ||
        String(b?.bundleId) === String(bundleId),
    );

    const requestDataItem = bundle?.finalRequestData?.request?.find(
      (req: any) =>
        req?.requestId == requestId ||
        req?.id == requestId ||
        String(req?.requestId) === String(requestId) ||
        String(req?.id) === String(requestId),
    );

    if (!requestDataItem) {
      console.error(`Could not find request for variable {{${variable.name}}}`);
      processNextVariable();
      return;
    }

    /**  CONFIG + DATA  */
    let data = {};
    let config;

    if (selectedConfig) {
      const configToUse = Array.isArray(selectedConfig)
        ? selectedConfig[0]
        : selectedConfig;

      data = Object.fromEntries(
        Object.entries(configToUse || {}).filter(
          ([key]) => !EXCLUDE_DATA_KEYS.includes(key),
        ),
      );

      config = {
        tabId: configToUse?.tabId,
        uid: configToUse?.uid,
        configType: "temp",
      };
    } else {
      data = bundle?.finalRequestData?.data || bundle?.data || {};
      config = savedSettings || bundle?.config || {};
    }

    const pluginId = requestDataItem?.plugin_id || bundle?.plugin_id || "";

    const configId =
      selectedConfig?.uid || requestDataItem?.configuration_Id || "";

    /**  INTERPOLATION  */
    const interpolatedHeaders = (requestDataItem.headers || []).map(
      (h: any) => ({
        key: interpolateDefinedVariablesOnly(h.key, definedVariables),
        value: interpolateDefinedVariablesOnly(h.value, definedVariables),
      }),
    );

    const interpolatedUrl = interpolateDefinedVariablesOnly(
      requestDataItem.url || "",
      definedVariables,
    );

    const interpolatedToken = interpolateDefinedVariablesOnly(
      requestDataItem.auth?.token || "",
      definedVariables,
    );

    /**  BODY HANDLING  */
    let bodyType =
      requestDataItem.body_type || requestDataItem.bodyType || "none";

    if (bodyType === "form-urlencoded") {
      bodyType = "x-www-form-urlencoded";
    }

    let interpolatedBody: any = "";

    if (bodyType === "x-www-form-urlencoded") {
      if (
        typeof requestDataItem.body === "object" &&
        !Array.isArray(requestDataItem.body) &&
        requestDataItem.body !== null
      ) {
        const formObj: any = {};
        Object.entries(requestDataItem.body).forEach(([key, value]) => {
          formObj[key] = interpolateDefinedVariablesOnly(
            String(value || ""),
            definedVariables,
          );
        });
        interpolatedBody = formObj;
      } else if (Array.isArray(requestDataItem.formData)) {
        const formObj: any = {};
        requestDataItem.formData.forEach((item: any) => {
          if (item.enabled !== false) {
            formObj[item.key] = interpolateDefinedVariablesOnly(
              item.value || "",
              definedVariables,
            );
          }
        });
        interpolatedBody = formObj;
      } else {
        interpolatedBody = {};
      }
    } else {
      interpolatedBody = interpolateDefinedVariablesOnly(
        requestDataItem.body || "",
        definedVariables,
      );
    }

    const rawType =
      bodyType === "raw"
        ? (requestDataItem.raw_type ?? requestDataItem.rawBodyType ?? null)
        : null;

    /**  QUERY PARAMS (MERGED WITH URL)  */
    const interpolatedQueryParams = (requestDataItem.queryParams || []).map(
      (param: any) => ({
        enabled: param.enabled ?? true,
        key: interpolateDefinedVariablesOnly(param.key || "", definedVariables),
        value: interpolateDefinedVariablesOnly(
          param.value || "",
          definedVariables,
        ),
      }),
    );

    const urlQueryParams: Array<{ key: string; value: string }> = [];

    try {
      const urlObj = new URL(interpolatedUrl);
      urlObj.searchParams.forEach((value, key) => {
        urlQueryParams.push({ key, value });
      });
    } catch (e) {
      console.error(`Could not parse URL: ${interpolatedUrl}`);
    }

    const allQueryParams = [...interpolatedQueryParams];

    urlQueryParams.forEach((urlParam) => {
      const index = allQueryParams.findIndex((p) => p.key === urlParam.key);

      if (index >= 0) {
        allQueryParams[index] = { ...urlParam, enabled: true };
      } else {
        allQueryParams.push({ ...urlParam, enabled: true });
      }
    });

    const params =
      allQueryParams.length > 0
        ? {
            type: "params",
            parameter: allQueryParams.map((p: any) => ({
              key: p.key,
              value: p.value,
            })),
          }
        : {
            type: "none",
            parameter: [],
          };

    /**  AUTH  */
    const authTypeMapping: Record<string, string> = {
      basic: "basic_auth",
      apiKey: "api_key",
      bearer: "bearer_token",
      jwt: "jwt_token",
      none: "none",
    };

    const rawAuthType = requestDataItem.auth?.type || "";
    const mappedAuthType = authTypeMapping[rawAuthType] || rawAuthType;

    /**  PAYLOAD  */
    const payload = {
      requestId: requestDataItem.requestId,
      plugin_id: pluginId,
      url: interpolatedUrl,
      method: requestDataItem.method || "GET",
      configuration_Id: configId,
      params,
      headers: interpolatedHeaders,
      ssl_verify: requestDataItem.ssl_verify ?? true,
      auth: {
        type: mappedAuthType,
        ...(requestDataItem.auth?.token && { token: interpolatedToken }),
        ...(requestDataItem.auth?.username && {
          username: interpolateDefinedVariablesOnly(
            requestDataItem.auth?.username,
            definedVariables,
          ),
        }),
        ...(requestDataItem.auth?.password && {
          password: interpolateDefinedVariablesOnly(
            requestDataItem.auth?.password,
            definedVariables,
          ),
        }),
      },
      body: interpolatedBody !== "" ? interpolatedBody : null,
      body_type: bodyType,
      raw_type: rawType,
      isLoop: requestDataItem.isLoop || false,
      isSave: requestDataItem.isSave || false,
      loopVariable: requestDataItem.loopVariable || null,
      loopSourcePath: requestDataItem.loopSourcePath || null,
      loopSourceRequest: requestDataItem.loopSourceRequest || null,
      loopSourceRequestBundle: requestDataItem.loopSourceRequestBundle || null,
      checkpoint: requestDataItem.checkpoint || {
        produces: { enabled: false, sourcePath: null },
        consumes: { enabled: false, paramTemplate: null },
      },
      chainedFrom: requestDataItem.chainedFrom || null,
      flag: 1,
      config,
      data,
      pagination: requestDataItem.pagination || { type: "none" },
      filteration: requestDataItem.filteration
        ? {
            type: requestDataItem.filteration.type || "none",
            default_cron_start_time:
              requestDataItem.filteration.default_cron_start_time || "none",
          }
        : {
            type: "none",
            default_cron_start_time: "none",
          },
      ...(requestDataItem.mappingId && {
        mappingId: requestDataItem.mappingId,
      }),
      ...(requestDataItem.datalistId && {
        datalistId: requestDataItem.datalistId,
      }),
      ...(requestDataItem.type_threatlookup && {
        type_threatlookup: requestDataItem.type_threatlookup,
      }),
    };

    setAuthVariable.mutate(payload);
  };

  // Refresh all Action variables only
  const handleRefreshAllVariables = (selectedConfig?: any): boolean => {
    if (selectedConfig) selectedConfigRef.current = selectedConfig;

    const actionLogic = logicType?.find((logic) => logic?.name === "Action");

    if (!actionLogic?.data?.length) {
      toast({
        title: "No Action Logic Found",
        description: "There are no action bundles to refresh variables from.",
        variant: "destructive",
        duration: 3000,
      });
      return false;
    }

    // Only Action variables
    const actionVariables: DefinedVariable[] = actionLogic.data.flatMap(
      (bundle: any) =>
        (bundle?.finalRequestData?.variables || []).filter(
          (v: DefinedVariable) => v.variableType === "Action",
        ),
    );

    if (!actionVariables.length) {
      toast({
        title: "No Action Variables",
        description: "There are no Action variables to refresh.",
        variant: "info",
        duration: 3000,
      });
      return false;
    }

    // Skip if all Action variables already have values
    const allValuesPresent = actionVariables.every((variable) => {
      const existingVar = definedVariables?.find(
        (v) => v.name === variable.name,
      );
      return (
        existingVar && existingVar.value != null && existingVar.value !== ""
      );
    });

    if (allValuesPresent) {
      toast({
        title: "All Variables Up-to-Date",
        description: "Action variables are already refreshed.",
        variant: "success",
        duration: 3000,
      });
      return false;
    }

    // Initialize refresh queue
    refreshQueueRef.current = actionVariables;
    setCurrentRefreshIndex(0);

    toast({
      title: "Refreshing Action Variables ...",
      description: `Refreshing ${actionVariables.length} variable${actionVariables.length > 1 ? "s" : ""}...`,
      variant: "info",
    });

    // Start with first Action variable
    refreshSingleVariable(actionVariables[0], selectedConfigRef.current);
    return true;
  };

  // Function to sort fields with "submit" type last
  const sortFieldsWithSubmitLast = (fieldsList: FieldItem[]) => {
    if (!fieldsList || fieldsList.length === 0) return fieldsList;
    const nonSubmitFields = fieldsList.filter((f) => f.fieldType !== "submit");
    const submitFields = fieldsList.filter((f) => f.fieldType === "submit");
    return [...nonSubmitFields, ...submitFields];
  };

  return (
    <div
      className={`${
        meshVersion === "2.0" ? "min-h-0" : "h-full min-h-0"
      } flex flex-col`}
    >
      <div
        className={`text-sm flex flex-col p-4 rounded-2xl border-secondary border flex-1 min-h-0 shadow-[0_2px_4px_rgba(0,0,0,0.09)] ${className}`}
      >
        {/* confirmation */}
        <PageLoader show={loading} />
        {deleteActionModalOpen && (
          <Confirmation
            text="Are you sure you want to delete this Action?"
            confirmAction={onDelete}
            rejectAction={() => {
              setDeleteActionModalOpen(false);
              setActionToDeleteIndex(null);
            }}
            confirmText="Yes, delete it!"
            rejectText="Cancel"
            show={!!deleteActionModalOpen}
            closeDialog={() => {
              setDeleteActionModalOpen(false);
              setActionToDeleteIndex(null);
            }}
          />
        )}

        {/* Title */}
        <div
          className={`border-b border-secondary pb-2 w-full mb-6 flex items-center justify-between  ${meshVersion != "2.0" ? "bg-primary-background" : ""}`}
        >
          {meshVersion != "2.0" ? (
            <div className="p-1">Actions</div>
          ) : (
            <div className="flex items-center gap-3 mb-1">
              <div>
                <Zap size={17} className="text-muted-foreground" />
              </div>
              <div className=" flex flex-col">
                <p className="text-sm font-semibold">Actions</p>
                <p className="text-xs text-muted-foreground">
                  Create Plugin actions here.
                </p>
              </div>
            </div>
          )}

          {/* Add Button */}
          {!showForm && (
            <div
              onClick={() =>
                dispatch({
                  type: MeshPluginActions.SET_SHOW_FORM,
                  payload: true,
                })
              }
              className="w-fit cursor-pointer text-primaryBlue hover:text-muted-foreground transition flex items-center gap-1"
            >
              <CirclePlus size={15} />
              {fields?.length === 0 ? "Add Action Field" : "Add Another Field"}
            </div>
          )}
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto">
          {/* Render Saved Fields */}
          {fields && fields.length > 0 ? (
            <div className="space-y-4 mb-6 px-3">
              {sortFieldsWithSubmitLast(fields).map((field) => {
                const actualIndex = fields.findIndex(
                  (f) =>
                    f.fieldName === field.fieldName &&
                    f.fieldType === field.fieldType,
                );

                return (
                  <div key={actualIndex} className="w-full">
                    <div className="flex flex-col gap-2 max-w-[450px]">
                      {/* Field Display */}
                      <div className="flex items-center gap-1">
                        <div className="flex flex-col w-full">
                          <label className="ml-1 font-medium mb-1">
                            {field?.fieldType === "submit"
                              ? ` action submit button`
                              : field?.fieldName}
                          </label>

                          {/* Input Field */}
                          {field.fieldType === "input" && (
                            <input
                              type="text"
                              value={field.defaultValue || ""}
                              disabled
                              className="w-full p-2 border rounded-md bg-background focus:ring-secondary focus:ring-1"
                            />
                          )}

                          {/* Import Settings */}
                          {field.fieldType === "import_settings" && (
                            <Select
                              value={dropdownSelections?.[actualIndex] ?? ""}
                              onValueChange={(val) => {
                                setDropdownSelections?.((prev) => ({
                                  ...prev,
                                  [actualIndex]: val,
                                }));
                              }}
                            >
                              <SelectTrigger className="max-w-[450px]">
                                <SelectValue placeholder="Select a configuration" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectGroup>
                                  {fields
                                    ?.find(
                                      (item) =>
                                        item.fieldType == "import_settings",
                                    )
                                    ?.dropdownOptions?.map((i: any) => (
                                      <SelectItem key={i.value} value={i.value}>
                                        {i.label}
                                      </SelectItem>
                                    ))}
                                </SelectGroup>
                              </SelectContent>
                            </Select>
                          )}

                          {/* Nested Dropdown Tree (API-based) */}
                          {field.fieldType === "dropdown" &&
                            Array.isArray(field.dropdownTree) &&
                            field.dropdownTree.length > 0 && (
                              <div className="flex flex-col gap-2">
                                {field.dropdownTree &&
                                  field.dropdownTree.length > 0 && (
                                    <>
                                      {/* Root Option Selector */}
                                      <Select
                                        value={
                                          dropdownChains?.[actualIndex]?.[0] ||
                                          ""
                                        }
                                        onValueChange={(val) => {
                                          setDropdownChains?.((prev) => ({
                                            ...prev,
                                            [actualIndex]: { 0: val },
                                          }));
                                        }}
                                      >
                                        <SelectTrigger className="w-full">
                                          <SelectValue placeholder="Select an option" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectGroup>
                                            <SelectItem
                                              key="placeholder"
                                              value="select"
                                            >
                                              Select an option
                                            </SelectItem>
                                            {/*  Render all root options */}
                                            {field.dropdownTree.map(
                                              (node: any) => (
                                                <SelectItem
                                                  key={node.value}
                                                  value={node.value}
                                                >
                                                  {node.label}
                                                </SelectItem>
                                              ),
                                            )}
                                          </SelectGroup>
                                        </SelectContent>
                                      </Select>

                                      {/*  Render Target Fields when option is selected */}
                                      {(() => {
                                        const selectedRootValue =
                                          dropdownChains?.[actualIndex]?.[0];

                                        const selectedRootNode =
                                          field.dropdownTree.find(
                                            (node: any) =>
                                              node.value === selectedRootValue,
                                          );

                                        if (
                                          selectedRootNode &&
                                          selectedRootValue !== "select"
                                        ) {
                                          return renderDropdownTreeLevels(
                                            selectedRootNode,
                                            actualIndex,
                                            1,
                                          );
                                        }
                                        return null;
                                      })()}
                                    </>
                                  )}
                              </div>
                            )}

                          {/* Nested Dropdown Tree (config-based) */}

                          {field.fieldType === "dropdown" &&
                            Array.isArray(field.configDropdownTree) &&
                            field.configDropdownTree.length > 0 && (
                              <div className="flex flex-col gap-2">
                                {field.configDropdownTree &&
                                  field.configDropdownTree.length > 0 && (
                                    <>
                                      {/* Root Option Selector */}
                                      <Select
                                        value={
                                          dropdownChains?.[actualIndex]?.[0] ||
                                          ""
                                        }
                                        onValueChange={(val) => {
                                          setDropdownChains?.((prev) => ({
                                            ...prev,
                                            [actualIndex]: { 0: val },
                                          }));
                                        }}
                                      >
                                        <SelectTrigger className="w-full">
                                          <SelectValue placeholder="Select an option" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectGroup>
                                            <SelectItem
                                              key="placeholder"
                                              value="select"
                                            >
                                              Select an option
                                            </SelectItem>
                                            {/*Render all config root options */}
                                            {field.configDropdownTree.map(
                                              (node: any) => (
                                                <SelectItem
                                                  key={node.value}
                                                  value={node.value}
                                                >
                                                  {node.label}
                                                </SelectItem>
                                              ),
                                            )}
                                          </SelectGroup>
                                        </SelectContent>
                                      </Select>

                                      {/*  Render Target Fields when config option is selected */}
                                      {(() => {
                                        const selectedRootValue =
                                          dropdownChains?.[actualIndex]?.[0];

                                        const selectedRootNode =
                                          field.configDropdownTree.find(
                                            (node: any) =>
                                              node.value === selectedRootValue,
                                          );

                                        if (
                                          selectedRootNode &&
                                          selectedRootValue !== "select"
                                        ) {
                                          return renderDropdownTreeLevels(
                                            selectedRootNode,
                                            actualIndex,
                                            1,
                                          );
                                        }
                                        return null;
                                      })()}
                                    </>
                                  )}
                              </div>
                            )}

                          {/*  API Dropdown Tree  */}
                          {field.fieldType === "dropdown" &&
                            Array.isArray(field.dropdownTree) &&
                            field.dropdownTree.length > 0 && (
                              <div className="flex flex-col gap-2">
                                {/* API dropdown rendering code from above */}
                              </div>
                            )}

                          {/*  Config Dropdown Tree  */}
                          {field.fieldType === "dropdown" &&
                            configValue &&
                            !field.dropdownTree &&
                            Array.isArray(field.configDropdownTree) &&
                            field.configDropdownTree.length > 0 && (
                              <div className="flex flex-col gap-2">
                                {/* Config dropdown rendering code from above */}
                              </div>
                            )}

                          {/* Simple Dropdown*/}
                          {field.fieldType === "dropdown" &&
                            (!field.dropdownTree ||
                              field.dropdownTree.length === 0) &&
                            (!field.configDropdownTree ||
                              field.configDropdownTree.length === 0) && (
                              <Select
                                value={dropdownSelections?.[actualIndex] ?? ""}
                                onValueChange={(val) => {
                                  setDropdownSelections?.((prev) => ({
                                    ...prev,
                                    [actualIndex]: val,
                                  }));
                                }}
                              >
                                <SelectTrigger className="max-w-[450px]">
                                  <SelectValue placeholder="Select an option" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectGroup>
                                    <SelectItem
                                      key="placeholder"
                                      value="select"
                                    >
                                      Select an option
                                    </SelectItem>
                                    {field.dropdownOptions?.map((opt) => (
                                      <SelectItem
                                        key={opt.value}
                                        value={opt.value}
                                      >
                                        {opt.label}
                                      </SelectItem>
                                    ))}
                                  </SelectGroup>
                                </SelectContent>
                              </Select>
                            )}

                          {/* Submit Button - Show Button instead of Select */}
                          {field.fieldType === "submit" && (
                            <Button className="w-fit">
                              {field.fieldName || "Submit Action"}
                            </Button>
                          )}
                        </div>

                        {/* Edit / Delete Buttons */}
                        <div className="flex items-center gap-2 mt-6">
                          <SquarePen
                            className="cursor-pointer text-foreground  hover:scale-110 transition-transform"
                            size={14}
                            onClick={() => handleEdit(actualIndex)}
                          />
                          <Trash2
                            className="cursor-pointer text-primary  hover:scale-110 transition-transform"
                            size={15}
                            onClick={() => removeField(actualIndex)}
                          />
                        </div>
                      </div>

                      {/* Extra Dependent Dropdown */}
                      {dropdownSelections &&
                        dropdownSelections[actualIndex] &&
                        field.dropdownOptions &&
                        field.dropdownOptions.length > 0 &&
                        !!field.dropdownOptions.find(
                          (opt) =>
                            opt.value === dropdownSelections[actualIndex],
                        ) &&
                        (field.dropdownOptions.find(
                          (opt) =>
                            opt.value === dropdownSelections[actualIndex],
                        )!.resValues?.length ?? 0) > 0 && (
                          <div className="flex items-center gap-1">
                            <div className="flex-1">
                              <label className="ml-1 mt-1">
                                {getTargetLabel(field, actualIndex)}
                              </label>
                              <Select
                                value={
                                  (dependentSelections ?? {})[actualIndex] ?? ""
                                }
                                onValueChange={(val) => {
                                  setDependentSelections?.((prev) => ({
                                    ...prev,
                                    [actualIndex]: val,
                                  }));
                                }}
                              >
                                <SelectTrigger className="w-full">
                                  <SelectValue placeholder="Select dependent option" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectGroup>
                                    <SelectItem
                                      key="placeholder"
                                      value="select"
                                    >
                                      Select an option
                                    </SelectItem>
                                    {field.dropdownOptions?.map(
                                      (opt, optIndex) => (
                                        <SelectItem
                                          key={opt.value}
                                          value={opt.value}
                                        >
                                          <div className="flex justify-between items-center w-full">
                                            <span>{opt.label}</span>
                                            <SquarePen
                                              className="ml-2 cursor-pointer text-muted hover:scale-110 transition-transform"
                                              size={14}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                editDropdownOption(
                                                  actualIndex,
                                                  optIndex,
                                                );
                                              }}
                                            />
                                          </div>
                                        </SelectItem>
                                      ),
                                    )}
                                  </SelectGroup>
                                </SelectContent>
                              </Select>
                            </div>

                            <div className="flex items-center gap-2 mt-6">
                              <SquarePen
                                className="cursor-pointer text-primary hover:scale-110 transition-transform"
                                size={14}
                                onClick={() => handleEdit(actualIndex)}
                              />
                              <Trash2
                                className="cursor-pointer text-primary hover:scale-110 transition-transform"
                                size={15}
                                onClick={() => removeField(actualIndex)}
                              />
                            </div>
                          </div>
                        )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <FallBackUi
              title="No Action fields created yet !"
              description="Click 'Add Action Field' to create required plugin action fields ."
            />
          )}

          {/* Field Creation Form */}
          {showForm && (
            <CreateActionField
              dropdownOptions={dropdownOptions}
              setDropdownOptions={(value) =>
                dispatch({
                  type: MeshPluginActions.SET_DROPDOWN_OPTIONS,
                  payload: value,
                })
              }
              dropdownInput={dropdownInput}
              setDropdownInput={setDropdownInput}
              handleAddField={handleAddField}
              handleRemoveDropdownOption={handleRemoveDropdownOption}
              handleAddDropdownOption={handleAddDropdownOption}
              dependsOnField={dependsOnField}
              setDependsOnField={setDependsOnField}
              definedVariables={definedVariables}
              prefillData={fields?.[editIndex!]}
              isApiValue={isApiValue}
              setIsApiValue={setIsApiValue}
              configValue={configValue}
              setConfigValue={setConfigValue}
              isFromVariable={isFromVariable}
              setIsFromVariable={setIsFromVariable}
              editIndex={editIndex}
              setEditIndex={setEditIndex}
              open={showForm}
              handleClose={handleClose}
              logicType={logicType}
              isFromAnomaly={isFromAnomaly}
              setIsFromAnomaly={setIsFromAnomaly}
              mainDropdownOptions={mainDropdownOptions}
              setMainDropdownOptions={setMainDropdownOptions}
              targetDropdownMap={targetDropdownMap}
              setTargetDropdownMap={setTargetDropdownMap}
              dropdownBlocks={dropdownBlocks}
              setDropdownBlocks={setDropdownBlocks}
              alldropdownTrees={alldropdownTrees}
              setAlldropdownTrees={setAlldropdownTrees}
              fields={fields}
              handleRefreshAllVariables={handleRefreshAllVariables}
              submissionAction={submissionAction}
              setSubmissionAction={setSubmissionAction}
              meshVersion={meshVersion}
              actionBundlesOrWorkflows={actionBundlesOrWorkflows}
              settingsBundlesOrWorkflows={settingsBundlesOrWorkflows}
              pluginId={
                sessionStorage.getItem("meshPluginId") ||
                sessionStorage.getItem("editPluginId") ||
                "-- not found in action page --"
              }
            />
          )}
        </div>
      </div>
      {meshVersion !== "2.0" && (
        <div className="flex justify-end w-full mt-auto pt-3">
          <Button onClick={handleSaveSettings}>Save Actions</Button>
        </div>
      )}
    </div>
  );
};

export default ActionsPage;
